import { countriesList } from '@cmg/common';
import { Divider, Section, Stack } from '@cmg/design-system';
import defaultTo from 'lodash/defaultTo';
import { useMemo } from 'react';

import { useIsLeftLeadEnabledOnOffering } from '../../../common/hooks/useIsLeftLeadEnabledOnOffering';
import { useIsSettlementAgentEnabledOnOffering } from '../../../common/hooks/useIsSettlementAgentEnabledOnOffering/useIsSettlementAgentEnabledOnOffering';
import { ManagerRole } from '../../../graphql';
import { managerRoleLabels } from '../../../types/domain/manager/constants';
import { OfferingDetails_PublishedOffering_SyndicateFragment } from '../../offering-side-panel/offering-details/graphql/__generated__';
import { useManagerResponsibilities } from '../hooks/useManagerResponsibilities';
import { KeyValuePair } from './KeyValuePair';

type ManagersSectionProps = {
  syndicate: OfferingDetails_PublishedOffering_SyndicateFragment | null;
  offeringCountryCode: string;
};

const SEPARATOR = ', ';

export function ManagersSection({
  syndicate,
  offeringCountryCode,
}: Readonly<ManagersSectionProps>) {
  const managersResponsibilities = useManagerResponsibilities(syndicate);

  const managerRoles = useMemo(() => {
    if (!syndicate?.managers) return;
    return syndicate.managers.reduce<{ [role in ManagerRole]?: string }>((acc, manager) => {
      if (!acc[manager.role]) {
        acc[manager.role] = manager.firmName;
        return acc;
      }
      if (manager.role) {
        acc[manager.role] += `${SEPARATOR}${manager.firmName}`;
      }
      return acc;
    }, {});
  }, [syndicate?.managers]);

  const isUsOffering = offeringCountryCode === countriesList.Usa.countryCode;
  const { isLeftLeadEnabled, loading: isLeftLeadLoading } =
    useIsLeftLeadEnabledOnOffering(offeringCountryCode);
  const { isSettlementAgentEnabled, loading: isSettlementAgentLoading } =
    useIsSettlementAgentEnabledOnOffering(offeringCountryCode);

  return (
    <Section title="Managers">
      {!isLeftLeadLoading && isLeftLeadEnabled && (
        <KeyValuePair label="Left Lead" value={defaultTo(managersResponsibilities.leftLead, '-')} />
      )}
      {!isUsOffering && (
        <KeyValuePair
          label={`${managerRoleLabels[ManagerRole.GlobalCoordinator]}(s)`}
          value={defaultTo(managerRoles?.[ManagerRole.GlobalCoordinator], '-')}
        />
      )}
      <KeyValuePair
        label={`${managerRoleLabels[ManagerRole.ActiveBookrunner]}(s)`}
        value={defaultTo(managerRoles?.[ManagerRole.ActiveBookrunner], '-')}
      />
      <KeyValuePair
        label={`${managerRoleLabels[ManagerRole.Bookrunner]}(s)`}
        value={defaultTo(managerRoles?.[ManagerRole.Bookrunner], '-')}
      />
      <KeyValuePair
        label={`${managerRoleLabels[ManagerRole.CoLead]}(s)`}
        value={defaultTo(managerRoles?.[ManagerRole.CoLead], '-')}
      />
      <KeyValuePair
        label={`${managerRoleLabels[ManagerRole.CoManager]}(s)`}
        value={defaultTo(managerRoles?.[ManagerRole.CoManager], '-')}
      />
      <KeyValuePair
        label={`${managerRoleLabels[ManagerRole.Underwriter]}(s)`}
        value={defaultTo(managerRoles?.[ManagerRole.Underwriter], '-')}
      />
      <Stack py={1}>
        <Divider />
      </Stack>
      {!isSettlementAgentLoading && isSettlementAgentEnabled && (
        <KeyValuePair
          label="Settlement Agent"
          value={defaultTo(managersResponsibilities.settlementAgent, '-')}
        />
      )}
      <KeyValuePair
        label="Stabilization Agent"
        value={defaultTo(managersResponsibilities.stabilizationAgent, '-')}
      />
      <KeyValuePair
        label="Logistics Agent"
        value={defaultTo(managersResponsibilities.logisticsAgent, '-')}
      />
    </Section>
  );
}
